import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function FeaturedArticles() {
  const [articles, setArticles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  
  const [startX, setStartX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await fetch('https://tbido-website-api.azurewebsites.net/getActiveArticles');
        if (!response.ok) throw new Error('Failed to fetch articles');
        const data = await response.json();
        
        if (data.articles && data.articles.length > 0) {
          setArticles(data.articles.slice(0, 5));
        }
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  useEffect(() => {
    if (articles.length === 0 || isDragging) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % articles.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [articles.length, isDragging]);

  const createSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '');
  };

  const getCardStyle = (index) => {
    const total = articles.length;
    if (total === 0) return "hidden";
    
    let offset = (index - currentIndex + total) % total;
    
    if (offset === total - 1) offset = -1;
    if (offset === total - 2) offset = -2;

    if (offset === 0) {
      return "z-30 scale-100 opacity-100 translate-x-0 blur-none";
    } else if (offset === -1) {
      return "z-20 scale-[0.85] opacity-50 -translate-x-[30%] sm:-translate-x-[12%] md:-translate-x-[20%] blur-[3px] cursor-pointer"; 
    } else if (offset === 1) {
      return "z-20 scale-[0.85] opacity-50 translate-x-[30%] sm:translate-x-[12%] md:translate-x-[20%] blur-[3px] cursor-pointer"; 
    }
    return "z-0 scale-75 opacity-0 translate-x-0 hidden"; 
  };

  const handleDragStart = (e) => {
    setIsDragging(true);
    setStartX(e.type.includes('mouse') ? e.pageX : e.touches[0].pageX);
  };

  const handleDragEnd = (e) => {
    if (!isDragging) return;
    setIsDragging(false);
    
    const endX = e.type.includes('mouse') ? e.pageX : (e.changedTouches ? e.changedTouches[0].pageX : startX);
    const diff = startX - endX;

    if (diff > 50) {
      setCurrentIndex((prev) => (prev + 1) % articles.length);
    } else if (diff < -50) {
      setCurrentIndex((prev) => (prev - 1 + articles.length) % articles.length);
    }
  };

  if (loading || articles.length === 0) return null;

  return (
    <section className="py-16 sm:py-20 overflow-hidden bg-white">
      <div className="w-[100%] max-w-6xl 2xl:max-w-5xl mx-auto px-4 sm:px-6 md:px-8">
        
        <div className="text-center mb-10 sm:mb-14" data-aos="fade-up">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-light text-gray-900">
            Our Latest <span className="font-bold text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%)' }}>Articles</span>
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mt-2">Swipe through the latest stories and updates from our community.</p>
        </div>

        <div 
          className="relative w-full h-[300px] sm:h-[400px] md:h-[450px] flex items-center justify-center cursor-grab active:cursor-grabbing"
          onMouseDown={handleDragStart}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchEnd={handleDragEnd}
        >
          {articles.map((article, index) => (
            <div
              key={article._id || index}
              className={`absolute w-[95%] sm:w-[85%] md:w-[80%] lg:w-[80%] h-full transition-all duration-700 ease-in-out ${getCardStyle(index)}`}
              onClick={() => {
                if (!isDragging) setCurrentIndex(index);
              }}
            >
              <div className="w-full h-full rounded-2xl shadow-2xl overflow-hidden relative group">
                <img src={article.articleImage} alt={article.articleTitle} draggable="false" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent pointer-events-none"></div>
                
                <div className="flex flex-row absolute top-3 sm:top-4 right-3 sm:right-4 pointer-events-none">
                  {article.articleSDG?.map((sdg, i) => (
                    <img 
                      key={i}
                      src={`/images/SDGs/SDG${sdg}.jpg`} 
                      alt="UNDP" 
                      className="h-8 sm:h-10 ml-2 md:h-12 w-auto"
                    />
                  ))}
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 md:p-8 pointer-events-none">
                  {index === 0 && (
                    <span className="px-3 py-1 bg-[#6D0C22] text-white text-[10px] sm:text-xs font-bold uppercase tracking-wider rounded-full shadow-md mb-2 sm:mb-3 inline-block">
                      Featured
                    </span>
                  )}
                  <div className="pointer-events-auto">
                    <Link to={`/blogs/${createSlug(article.articleTitle)}-${article._id || article.id}`}>
                      <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-1 sm:mb-2 leading-tight line-clamp-2 sm:line-clamp-none hover:text-gray-200 transition-colors">
                        {article.articleTitle}
                      </h3>
                    </Link>
                  </div>

                  <div className="hidden md:block mb-4">
                    <p className="text-sm text-gray-200 line-clamp-2">
                      {article.excerpt || article.articleDescription}
                    </p>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4 mt-2">
                    
                    <p className="text-xs sm:text-sm text-gray-300 font-medium md:hidden">
                      {new Date(article.articleDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>

                    <div className="hidden md:block text-[11px] md:text-sm text-gray-300">
                      <p className="text-[9px] md:text-xs text-gray-400 mb-0.5 md:mb-1">Written by:</p>
                      <p className="font-medium mb-1 md:mb-2 text-white">{article.articleAuthor}</p>
                      <p className="text-[9px] md:text-xs text-gray-400 mb-0.5 md:mb-1">Published on:</p>
                      <p className="text-white">{new Date(article.articleDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    </div>

                    <div className="pointer-events-auto w-full sm:w-auto">
                      <Link 
                        to={`/blogs/${createSlug(article.articleTitle)}-${article._id || article.id}`}
                        className="bg-white px-3 sm:px-5 md:px-6 py-1.5 sm:py-2 rounded-full font-semibold hover:bg-gray-100 transition text-[11px] sm:text-sm inline-block w-full text-center"
                      >
                        <span 
                          className="font-normal bg-clip-text text-transparent"
                          style={{
                            backgroundImage: 'linear-gradient(to right, #6D0C22 0%, #0E386B 92%)'
                          }}
                        >
                          Read Full Article
                        </span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}